import type { ControlsProps, ESettingsGroup, EWidgetSection } from '#shared';

export const widgetRegistry = new Map<
  keyof WidgetRegistry,
  WidgetRegistry[keyof WidgetRegistry]
>();

export type WidgetId<T extends WidgetType = WidgetType> = `${T}-${string}`;
export interface WidgetInstance<T extends WidgetType = WidgetType> {
  id: WidgetId;
  type: T;
  title: string;
  sectionId: EWidgetSection | null;
  sealed?: boolean;
  settings?: WidgetSettings<WidgetRegistry[T]['config']>;
  visible?: boolean;
  /** Instance index for multi-instance widgets (e.g., 0=left aside, 1=right aside) */
  instanceIndex?: number;
}

export type WidgetConfigBase<
  T extends Record<string, unknown> = Record<string, unknown>,
  K extends string = keyof T extends string ? keyof T : string,
> = Record<K, ControlsProps<K>>;

export type WidgetSettings<T extends WidgetConfigBase> = {
  [K in keyof T]: T[K]['value'];
} & {
  shareSettings?: boolean; // Additional common settings that apply to all widget types
};

// Type utilities for working with widget configurations
export interface SettingsConfig<
  T extends Record<string, unknown> = Record<string, unknown>,
  N extends WidgetType = WidgetType,
  K extends string = keyof T extends string ? keyof T : string,
> {
  group: ESettingsGroup;
  name: N;
  config: Record<K, ControlsProps<K>>;
}
